import { BattleScene } from "../BattleScene";
import { MineActor } from "../turret-actors/shot-actors/MineActor";
import { EnemyActor } from "../enemy-actors/EnemyActor";
import { TurretActor } from "../turret-actors/TurretActor";
import { Board } from "./Board";
import { GameConstants } from "../../../GameConstants";
import { ArrowActor } from "../turret-actors/shot-actors/ArrowActor";
import { GameVars } from "../../../GameVars";
import { MortarActor } from "../turret-actors/shot-actors/MortarActor";
import { BattleManager } from "../BattleManager";
import { GlueBulletActor } from "../turret-actors/shot-actors/GlueBulletActor";
import { AudioManager } from "../../../AudioManager";
import { MortarTurretActor } from "../turret-actors/MortarTurretActor";
import { SplatTurretActor } from "../turret-actors/SplatTurretActor";
import { BzztTurretActor } from "../turret-actors/BzztTurretActor";
import { FireTurretActor } from "../turret-actors/FireTurretActor";
import { MinesTurretActor } from "../turret-actors/MinesTurretActor";
import { RocketsTurretActor } from "../turret-actors/RocketsTurretActor";
import { TeleportTurretActor } from "../turret-actors/TeleportTurretActor";
import * as Anuto from "../../../../engine/src";
import { GluePool } from "../turret-actors/shot-actors/GluePool";
import { RocketActor } from "../turret-actors/shot-actors/RocketActor";
import { SuicideSoldierActor } from "./SuicideSoldierActor";
import { CrossbowTurretActor } from "../turret-actors/CrossbowTurretActor";
import { LaserBeam } from "../turret-actors/shot-actors/LaserBeam";
import { FireBeam } from "../turret-actors/shot-actors/FireBeam";
import { CoinsEnemy } from "./CoinsEnemy";

export class BoardContainer extends Phaser.GameObjects.Container {

    public static currentInstance: BoardContainer;

    public board: Board;
    public actorsContainer: Phaser.GameObjects.Container;
    public enemyActors: EnemyActor[];
    
    private deadEnemyActors: EnemyActor[];
    private turretActors: TurretActor[];
    private arrowActors: ArrowActor[];
    private glueBulletActors: GlueBulletActor[];
    private mortarActors: MortarActor[];
    private rocketActors: RocketActor[];
    private mineActors: MineActor[];
    private suicideSoldierActor: SuicideSoldierActor;

    private rangeCircles: Phaser.GameObjects.Image[];
    private pointerContainer: Phaser.GameObjects.Container;
    private glueCirclesContainer: Phaser.GameObjects.Container;
    private circlesContainer: Phaser.GameObjects.Container;

    private gluePools: GluePool[];

    constructor(scene: Phaser.Scene) {

        super(scene);

        BoardContainer.currentInstance = this;

        this.x = GameConstants.GAME_WIDTH / 2;
        this.y = GameConstants.GAME_HEIGHT / 2 + GameConstants.CELLS_SIZE / 2;

        this.deadEnemyActors = [];
        this.enemyActors = [];
        this.turretActors = [];
        this.arrowActors = [];
        this.glueBulletActors = [];
        this.mortarActors = [];
        this.rocketActors = [];
        this.mineActors = [];
        this.rangeCircles = [];
        this.suicideSoldierActor = null;
        this.gluePools = [];

        this.board = new Board(this.scene);
        this.add(this.board);

        if (GameConstants.SHOW_DEBUG_GEOMETRY) {
            this.drawDebugGeometry();
        }

        this.circlesContainer = new Phaser.GameObjects.Container(this.scene);
        this.board.add(this.circlesContainer);

        this.pointerContainer = new Phaser.GameObjects.Container(this.scene);
        this.board.add(this.pointerContainer);

        this.glueCirclesContainer = new Phaser.GameObjects.Container(this.scene);
        this.board.add(this.glueCirclesContainer);

        this.board.bringToTop(this.board.foregroundRenderTexture);

        this.actorsContainer = new Phaser.GameObjects.Container(this.scene);
        this.board.add(this.actorsContainer);

        this.createAnimations();
        this.createEndFlags();

        if (!this.scene.sys.game.device.os.desktop) {
            this.scaleX = GameVars.scaleX + GameVars.contrascaleX;
        }

        if (GameVars.usePrevTurrets) {
            this.createInitialTurrets();
        }
    }

    public update(time: number, delta: number): void {

        // eliminar a los enemigos que ya han muerto
        for (let i = 0; i < this.deadEnemyActors.length; i++) {
            const index = this.enemyActors.indexOf(this.deadEnemyActors[i]);
            if (index !== -1) {
                this.enemyActors.splice(index, 1);
            }
        }

        this.board.update(time, delta);

        this.enemyActors.forEach(function (enemy) {
            enemy.update(time, delta);
        }); 

        this.turretActors.forEach(function (tower) {
            tower.update(time, delta);
        }); 

        this.arrowActors.forEach(function (arrow: ArrowActor) {
            arrow.update(time, delta);
        }); 

        this.glueBulletActors.forEach(function (bullet) {
            bullet.update(time, delta);
        }); 

        this.mortarActors.forEach(function (mortar) {
            mortar.update(time, delta);
        }); 

        this.rocketActors.forEach(function (rocket) {
            rocket.update(time, delta);
        }); 

        this.actorsContainer.sort("y");

        this.mineActors.forEach(function (mine) {
            mine.update(time, delta);
        });

        for (let i = 0; i < this.enemyActors.length; i++) {
            if (this.enemyActors[i].isTeleporting()) {
                this.actorsContainer.bringToTop(this.enemyActors[i]);
            }
        }

        if (this.suicideSoldierActor) {
            this.suicideSoldierActor.update(time, delta);
        }
    }

    public addEnemy(anutoEnemy: Anuto.Enemy, position: {r: number, c: number}): void {

        let enemyActor = new EnemyActor(this.scene, anutoEnemy, position);
        
        if (enemyActor) {
            this.actorsContainer.add(enemyActor);
            this.enemyActors.push(enemyActor);

            if (anutoEnemy.subtype.includes("boss")) {
                BattleScene.currentInstance.showBossLayer(anutoEnemy.editorId);
            }
        }
    }

    public removeEnemy(id: number): void {

        let i: number;
        for (i = 0; i < this.enemyActors.length; i ++) {
            if (this.enemyActors[i].id === id) {
               break; 
            }
        }

        const enemy = this.enemyActors[i];

        if (enemy) {
            this.enemyActors.splice(i, 1);
            enemy.destroy();
        }
    }

    public addSuicideSoldier(anutoSuicideSoldier: Anuto.SuicideSoldier): void {

        this.suicideSoldierActor = new SuicideSoldierActor(this.scene, anutoSuicideSoldier);
        this.actorsContainer.add(this.suicideSoldierActor);
    }

    public throwDynamite(x: number, y: number): void {

        let dynamite = new Phaser.GameObjects.Image(this.scene, x, y - 20, "texture_atlas_1", "dynamite");
        dynamite.setAngle(Math.random() * 360);
        this.actorsContainer.add(dynamite);

        this.scene.tweens.add({
            targets: dynamite,
            y: y + 3,
            angle: dynamite.angle + 180,
            ease: Phaser.Math.Easing.Linear,
            duration: GameVars.timeStepFactor === 1 ? 200 : 50,
            onComplete: () => {

                dynamite.destroy();

                if (!this.scene) {
                    return;
                }

                const explosionEffect = this.scene.add.sprite(x, y + 3, "texture_atlas_3");
                explosionEffect.setScale(1);
                this.actorsContainer.add(explosionEffect);

                explosionEffect.anims.play("explosion");
                AudioManager.playSound("explosion_max");

                explosionEffect.on("animationcomplete", () => {
                    explosionEffect.destroy();        
                }, this);
            },
            onCompleteScope: this
        });
    }

    public onSuicideSoldierReachedExit(anutoSuicideSoldier: Anuto.SuicideSoldier): void {

        this.suicideSoldierActor.destroy();
        AudioManager.stopSound("pig_running");
        this.suicideSoldierActor = null;
    }

    public onSuicideSoldierExploded(anutoSuicideSoldier: Anuto.SuicideSoldier, enemies: Anuto.Enemy[]): void {

        this.suicideSoldierActor.detonate();

        this.suicideSoldierActor = null;

        for (let i = 0; i < enemies.length; i ++) {
            const enemyActor = this.getEnemyActorByID(enemies[i].id);
            enemyActor.hit(GameConstants.IMPACT_SUICIDAL_SOLDIER);
        }
    }

    public addTurret(type: string, position: {r: number, c: number}, skipBuild?: boolean, level?: number): void {

        let turret: TurretActor;
        let anutoTurret = BattleManager.addTurret(type, position, skipBuild, level);

        if (!anutoTurret) {
            return;
        }

        switch (type) {

            case Anuto.GameConstants.TURRET_PEA:
                turret = new CrossbowTurretActor(this.scene, position, anutoTurret);
                break;
            case Anuto.GameConstants.TURRET_MORTAR:
                turret = new MortarTurretActor(this.scene, position, anutoTurret);
                break;
            case Anuto.GameConstants.TURRET_SPLAT:
                turret = new SplatTurretActor(this.scene, position, anutoTurret);
                break;
            case Anuto.GameConstants.TURRET_BZZT:
                turret = new BzztTurretActor(this.scene, position, anutoTurret);
                break;
            case Anuto.GameConstants.TURRET_FIRE:
                turret = new FireTurretActor(this.scene, position, anutoTurret);
                break;
            case Anuto.GameConstants.TURRET_MINES:
                turret = new MinesTurretActor(this.scene, position, anutoTurret);
                break;
            case Anuto.GameConstants.TURRET_ROCKETS:
                turret = new RocketsTurretActor(this.scene, position, anutoTurret);
                break;
            case Anuto.GameConstants.TURRET_TELEPORT:
                turret = new TeleportTurretActor(this.scene, position, anutoTurret);
                break;
            default:
        }
        
        this.actorsContainer.add(turret);
        this.turretActors.push(turret);

        this.board.addTurretBackground(position);
    }

    public removeTurret(anutoTurret: Anuto.Turret): void {

        BattleManager.hideTurretMenu();
        this.hideRangeCircles();

        let i: number;
        for (i = 0; i < this.turretActors.length; i ++) {
            if (this.turretActors[i].id === anutoTurret.id) {
               break; 
            }
        }

        this.board.removeTurretBackground(anutoTurret.position);

        const turret = this.turretActors[i];

        if (turret) {
            this.turretActors.splice(i, 1);
            turret.destroy();
        }
    }

    public addBullet(anutoBullet: Anuto.Bullet, anutoProjectileTurret: Anuto.PeaTurret): void {

        const projectileTurretActor = <CrossbowTurretActor> this.getTurretActorByID(anutoProjectileTurret.id);
        projectileTurretActor.shootArrow();

        const arrowActor = new ArrowActor(this.scene, anutoBullet);
        this.board.add(arrowActor);

        this.arrowActors.push(arrowActor);
    }

    public addGlueBullet(anutoBullet: Anuto.GlueBullet, anutoProjectileTurret: Anuto.SplatTurret): void {

        const glueTurretActor = <SplatTurretActor> this.getTurretActorByID(anutoProjectileTurret.id);
        glueTurretActor.shootGlue();

        const bullet = new GlueBulletActor(this.scene, anutoBullet);
        this.board.add(bullet);

        this.glueBulletActors.push(bullet);
    }

    public addLaserBeam (anutoLaserTurret: Anuto.Turret, anutoEnemies: Anuto.Enemy[]): void {

        const laserTurretActor = <BzztTurretActor> this.getTurretActorByID(anutoLaserTurret.id);

        let enemyActors = [];

        for (let i = 0; i < anutoEnemies.length; i++) {
            enemyActors.push(this.getEnemyActorByID(anutoEnemies[i].id));
        }

        laserTurretActor.shootLaser(enemyActors);

        const laserBeam = new LaserBeam(this.scene, laserTurretActor, enemyActors);
        this.board.add(laserBeam);
    }

    public addFireBeam (anutoLaserTurret: Anuto.Turret, anutoEnemies: Anuto.Enemy[]): void {

        const fireTurretActor = <FireTurretActor> this.getTurretActorByID(anutoLaserTurret.id);

        let enemyActors = [];

        for (let i = 0; i < anutoEnemies.length; i++) {
            enemyActors.push(this.getEnemyActorByID(anutoEnemies[i].id));
        }

        fireTurretActor.shootFire(enemyActors);

        const fireBeam = new FireBeam(this.scene, fireTurretActor, enemyActors);
        this.board.add(fireBeam);
    }

    public addMortar(anutoMortar: Anuto.Mortar, anutoLaunchTurret: Anuto.MortarTurret): void {

        const launchTurretActor = <MortarTurretActor> this.getTurretActorByID(anutoLaunchTurret.id);
        launchTurretActor.shootMortar();

        const mortar = new MortarActor(this.scene, anutoMortar);
        this.board.add(mortar);

        this.mortarActors.push(mortar);
    }

    public addRocket(anutoMortar: Anuto.Mortar, anutoLaunchTurret: Anuto.RocketsTurret): void {

        const launchTurretActor = <RocketsTurretActor> this.getTurretActorByID(anutoLaunchTurret.id);
        launchTurretActor.shootMortar();

        const rocket = new RocketActor(this.scene, anutoMortar);
        this.board.add(rocket);

        this.rocketActors.push(rocket);
    }

    public addMine(anutoMine: Anuto.Mine, anutoLaunchTurret: Anuto.MinesTurret): void {

        const launchTurretActor = <MinesTurretActor> this.getTurretActorByID(anutoLaunchTurret.id);
        launchTurretActor.shootMine();

        const mine = new MineActor(this.scene, anutoMine, launchTurretActor);
        this.actorsContainer.add(mine);

        this.mineActors.push(mine);
    }

    public addGlue(anutoGlue: Anuto.Glue, anutoGlueTurret: Anuto.SplatTurret): void {

        const glueTurretActor = <SplatTurretActor> this.getTurretActorByID(anutoGlueTurret.id);
        glueTurretActor.shootGlue();

        const gluePool = new GluePool(this.scene, glueTurretActor, anutoGlue);
        this.glueCirclesContainer.add(gluePool);
        this.gluePools.push(gluePool);
    }

    public onGlueConsumed(anutoGlue: Anuto.Glue): void {

        let glue: GluePool = null;

        for (let i = 0; i < this.gluePools.length; i++) {

            if (anutoGlue.id === this.gluePools[i].id) {
                glue = this.gluePools[i];
                this.gluePools.splice(i, 1);
                glue.destroy();
            }
        }
    }

    public onEnemyHit(anutoEnemy: Anuto.Enemy, impactType: string): void {

        // encontrar el enemigo en cuestion
        let enemy: EnemyActor = this.getEnemyActorByID(anutoEnemy.id);

        if (enemy) {
            enemy.hit(impactType);
        }
    }

    public onEnemyHeal(id: number): void {

        let enemy: EnemyActor = this.getEnemyActorByID(id);

        if (enemy) {
            enemy.heal();
        }
    }

    public onEnemySpeed(id: number): void {

        let enemy: EnemyActor = this.getEnemyActorByID(id);

        if (enemy) {
            enemy.speed();
        }
    }

    public onEnemyGlueHit(anutoEnemy: Anuto.Enemy): void {
        
        // encontrar el enemigo en cuestion
        let enemy: EnemyActor = this.getEnemyActorByID(anutoEnemy.id);

        if (enemy) {
            enemy.glueHit();
        }
    }

    public onEnemyKilled(anutoEnemy: Anuto.Enemy): void {

        let enemyActor: EnemyActor = this.getEnemyActorByID(anutoEnemy.id);

        if (enemyActor) {
            enemyActor.die();
            this.deadEnemyActors.push(enemyActor);
        }
    }

    public teleportEnemy(anutoEnemy: Anuto.Enemy, anutoGlueTurret: Anuto.TeleportTurret): void {

        let enemyActor: EnemyActor = this.getEnemyActorByID(anutoEnemy.id);

        if (enemyActor) {
            enemyActor.teleport(anutoGlueTurret);
            // AudioManager.playSound("t3_teleport");

            let teleport = this.scene.add.sprite(enemyActor.x, enemyActor.y - 10, "texture_atlas_3");
            this.board.add(teleport);

            teleport.anims.play("teleport_in");

            teleport.on("animationcomplete", () => {
                teleport.destroy();
            }, this);
        } 
    }

    public onArrowImpacted(anutoBullet: Anuto.Bullet): void {

        let arrowActor: ArrowActor = null;

        for (let i = 0; i < this.arrowActors.length; i ++) {

            if (this.arrowActors[i].anutoBullet.id === anutoBullet.id) {
                arrowActor = this.arrowActors[i];
                break;
            }
        }

        if (arrowActor) {
            arrowActor.removeFromStage();
        }
    }

    public removeArrowActor(arrowActor: ArrowActor): void {
        
        const i = this.arrowActors.indexOf(arrowActor);
        this.arrowActors.splice(i, 1);
        arrowActor.destroy();
    }

    public removeGlueBullet(anutoBullet: Anuto.GlueBullet): void {

        let bulletActor: GlueBulletActor = null;

        for (let i = 0; i < this.glueBulletActors.length; i ++) {

            if (this.glueBulletActors[i].anutoGlueBullet.id === anutoBullet.id) {
                bulletActor = this.glueBulletActors[i];
                break;
            }
        }

        if (bulletActor) {
            const i = this.glueBulletActors.indexOf(bulletActor);
            this.glueBulletActors.splice(i, 1);
            bulletActor.destroy();
        }
    }

    public detonateMortar(anutoMortar: Anuto.Mortar): void {

        let mortarActor: MortarActor = null;

        for (let i = 0; i < this.mortarActors.length; i ++) {

            if (this.mortarActors[i].anutoMortar.id === anutoMortar.id) {
                mortarActor = this.mortarActors[i];
                break;
            }
        }

        if (mortarActor) {
            mortarActor.detonate();
        }

        let rocketActor: RocketActor = null;

        for (let i = 0; i < this.rocketActors.length; i ++) {

            if (this.rocketActors[i].anutoMortar.id === anutoMortar.id) {
                rocketActor = this.rocketActors[i];
                break;
            }
        }

        if (rocketActor) {
            rocketActor.detonate();
        }
    }

    public detonateMine(anutoMine: Anuto.Mine): void {

        let mineActor: MineActor = null;

        for (let i = 0; i < this.mineActors.length; i ++) {

            if (this.mineActors[i].anutoMine.id === anutoMine.id) {
                mineActor = this.mineActors[i];
                break;
            }
        }

        if (mineActor) {
            mineActor.detonate();
        }
    }

    public removeMortar(mortarActor: MortarActor): void {

        const i = this.mortarActors.indexOf(mortarActor);
        this.mortarActors.splice(i, 1);
        mortarActor.destroy();
    }

    public removeRocket(rocketActor: RocketActor): void {

        const i = this.rocketActors.indexOf(rocketActor);
        this.rocketActors.splice(i, 1);
        rocketActor.destroy();
    }

    public removeMine(mineActor: MineActor): void {

        const i = this.mineActors.indexOf(mineActor);
        this.mineActors.splice(i, 1);
        mineActor.destroy();
    }

    public onTurretReady(id: number): void {

        const turretActor = this.getTurretActorByID(id);
        turretActor.onReady();
    }

    public setTowerToUpgrade(id: number): void {
        
        const turretActor = this.getTurretActorByID(id);
        turretActor.setToUpgrade();
    }

    public onTurretUpgraded(id: number): void {
        
        const turretActor = this.getTurretActorByID(id);
        turretActor.upgrade();
    }
    
    public getTurretActorByID(id: number): TurretActor {

        let turretActor = null;

        for (let i = 0; i < this.turretActors.length; i ++) {
            if (this.turretActors[i].id === id) {
                turretActor = this.turretActors[i];
                break;
            }
        }

        return turretActor;
    }

    public getEnemyActorByID(id: number): EnemyActor {

        let enemy = null;

        for (let i = 0; i < this.enemyActors.length; i ++) {
            if (this.enemyActors[i].id === id) {
                enemy = this.enemyActors[i];
                break;
            }
        }

        return enemy;
    }

    public createRangeCircle(range: number, x: number, y: number): Phaser.GameObjects.Image {

        let rangeCircle = new Phaser.GameObjects.Image(this.scene, x, y, "texture_atlas_1", "fx_circle");
        rangeCircle.alpha = .75;
        rangeCircle.setScale((range * 2) / 196);
        rangeCircle.visible = false;
        this.circlesContainer.add(rangeCircle);

        this.scene.tweens.add({
            targets: rangeCircle,
            alpha: .5,
            ease: Phaser.Math.Easing.Linear,
            duration: 1000,
            repeat: -1,
            yoyo: true
        });

        this.rangeCircles.push(rangeCircle);

        return rangeCircle;
    }

    public hideRangeCircles(): void {

        for (let i = 0; i < this.rangeCircles.length; i ++) {
            this.rangeCircles[i].visible = false;
        }
    }

    public onPointerDown(): void {

        if (BattleScene.currentInstance.pauseMenu.visible) {
            BattleScene.currentInstance.hidePauseMenu();
        } else {
            this.hideRangeCircles();
            BattleManager.hideTurretMenu();
        }
        
    }

    public addCoinsEnemy(x: number, y: number, coins: number): void {

        let coinsEnemy = new CoinsEnemy(this.scene, x, y, coins);
        this.board.add(coinsEnemy);
    }

    public createInitialTurrets(): void {

        if (GameVars.prevTurrets && GameVars.currentMapData.name === GameVars.prevTurrets.name) {

            for (let i = 0; i < GameVars.prevTurrets.turrets.length; i++) {

                let turret = GameVars.prevTurrets.turrets[i];

                BoardContainer.currentInstance.addTurret(turret.type, turret.p, true, turret.level);
            }
        }
        
    }

    private drawDebugGeometry(): void {
        
        const path = new Phaser.GameObjects.Graphics(this.scene);

        path.lineStyle(2, 0xFFA500);

        for (let i = 0; i < GameVars.enemiesPathsCells.length; i ++) {
            for (let j = 0; j < GameVars.enemiesPathsCells[i].path.length - 1; j ++) {

                let x = (GameVars.enemiesPathsCells[i].path[j].c + .5) * GameConstants.CELLS_SIZE;
                let y = (GameVars.enemiesPathsCells[i].path[j].r + .5) * GameConstants.CELLS_SIZE;
                
                path.moveTo(x, y);

                x = (GameVars.enemiesPathsCells[i].path[j + 1].c + .5) * GameConstants.CELLS_SIZE;
                y = (GameVars.enemiesPathsCells[i].path[j + 1].r + .5) * GameConstants.CELLS_SIZE;

                path.lineTo(x, y);

                path.stroke();
            }
        }

        this.board.add(path);
    }

    private createEndFlags(): void {

        for (let i = 0; i < GameVars.enemiesPathsCells.length; i++) {

            if (this.board.boardMatrix[GameVars.enemiesPathsCells[i].path[GameVars.enemiesPathsCells[i].path.length - 2].r - 1][GameVars.enemiesPathsCells[i].path[GameVars.enemiesPathsCells[i].path.length - 2].c] !== 2) {
                let img = new Phaser.GameObjects.Image(this.scene, GameConstants.CELLS_SIZE * GameVars.enemiesPathsCells[i].path[GameVars.enemiesPathsCells[i].path.length - 2].c + GameConstants.CELLS_SIZE / 2, (GameConstants.CELLS_SIZE * (GameVars.enemiesPathsCells[i].path[GameVars.enemiesPathsCells[i].path.length - 2].r - 1) + GameConstants.CELLS_SIZE / 2), "texture_atlas_1", "build_tower");
                img.setOrigin(.5, .75);
                this.actorsContainer.add(img);
            }
            
            if (this.board.boardMatrix[GameVars.enemiesPathsCells[i].path[GameVars.enemiesPathsCells[i].path.length - 2].r + 1][GameVars.enemiesPathsCells[i].path[GameVars.enemiesPathsCells[i].path.length - 2].c] !== 2) {
                let img = new Phaser.GameObjects.Image(this.scene, GameConstants.CELLS_SIZE * GameVars.enemiesPathsCells[i].path[GameVars.enemiesPathsCells[i].path.length - 2].c + GameConstants.CELLS_SIZE / 2, (GameConstants.CELLS_SIZE * (GameVars.enemiesPathsCells[i].path[GameVars.enemiesPathsCells[i].path.length - 2].r + 1) + GameConstants.CELLS_SIZE / 2), "texture_atlas_1", "build_tower");
                img.setOrigin(.5, .75);
                this.actorsContainer.add(img);
            }
        }
    }

    private createAnimations(): void {

        this.scene.anims.create({ key: "flag_build", frames: this.scene.anims.generateFrameNames( "texture_atlas_1", { prefix: "flag_build_", start: 1, end: 9, zeroPad: 1, suffix: ""}), frameRate: 12, repeat: 1});
        this.scene.anims.create({ key: "flag_build_loop", frames: this.scene.anims.generateFrameNames( "texture_atlas_1", { prefix: "flag_build_", start: 1, end: 9, zeroPad: 1, suffix: ""}), frameRate: 12, repeat: -1});
        this.scene.anims.create({ key: "upgrade_arrow", frames: this.scene.anims.generateFrameNames( "texture_atlas_1", { prefix: "upgrade_arrow_", start: 1, end: 12, zeroPad: 2, suffix: ""}), frameRate: 12, repeat: -1});

        this.scene.anims.create({ key: "glue_bullet", frames: this.scene.anims.generateFrameNames( "texture_atlas_1", { prefix: "bullet_3_1_", start: 1, end: 8, zeroPad: 1, suffix: ""}), frameRate: 12, repeat: -1});
        this.scene.anims.create({ key: "explosion", frames: this.scene.anims.generateFrameNames( "texture_atlas_3", { prefix: "explosion_", start: 0, end: 20, zeroPad: 2, suffix: ""}), frameRate: 30, repeat: 0});
        this.scene.anims.create({ key: "explosion_plus", frames: this.scene.anims.generateFrameNames( "texture_atlas_3", { prefix: "explosion_plus_", start: 0, end: 19, zeroPad: 2, suffix: ""}), frameRate: 60, repeat: 0});
        this.scene.anims.create({ key: "tar_spill", frames: this.scene.anims.generateFrameNames( "texture_atlas_1", { prefix: "tar_spill_", start: 1, end: 3, zeroPad: 4, suffix: ""}), frameRate: 30, repeat: 4});
        this.scene.anims.create({ key: "surfer_king", frames: this.scene.anims.generateFrameNames( "texture_atlas_1", { prefix: "surfer_king_", start: 1, end: 12, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        
        this.scene.anims.create({ key: "flame", frames: this.scene.anims.generateFrameNames( "texture_atlas_3", { prefix: "flame_", start: 0, end: 29, zeroPad: 2, suffix: ""}), frameRate: 30, repeat: -1});
        this.scene.anims.create({ key: "bolt", frames: this.scene.anims.generateFrameNames( "texture_atlas_3", { prefix: "bolt_", start: 0, end: 13, zeroPad: 2, suffix: ""}), frameRate: 60, repeat: 0});
        this.scene.anims.create({ key: "electric_damage", frames: this.scene.anims.generateFrameNames( "texture_atlas_3", { prefix: "electric_damage_", start: 0, end: 17, zeroPad: 2, suffix: ""}), frameRate: 30, repeat: 0});
        this.scene.anims.create({ key: "teleport_in", frames: this.scene.anims.generateFrameNames( "texture_atlas_3", { prefix: "teleport_in_", start: 1, end: 23, zeroPad: 2, suffix: ""}), frameRate: 60, repeat: 0});
        this.scene.anims.create({ key: "teleport_out", frames: this.scene.anims.generateFrameNames( "texture_atlas_3", { prefix: "teleport_out_", start: 1, end: 3, zeroPad: 2, suffix: ""}), frameRate: 60, repeat: 0});
        this.scene.anims.create({ key: "speed", frames: this.scene.anims.generateFrameNames( "texture_atlas_3", { prefix: "speed_fx_", start: 1, end: 19, zeroPad: 2, suffix: ""}), frameRate: 30, repeat: 0});
        this.scene.anims.create({ key: "water_1", frames: this.scene.anims.generateFrameNames( "texture_atlas_3", { prefix: "water_heal_", start: 22, end: 1, zeroPad: 2, suffix: ""}), frameRate: 30, repeat: 0});
        this.scene.anims.create({ key: "water_2", frames: this.scene.anims.generateFrameNames( "texture_atlas_3", { prefix: "water_heal_", start: 1, end: 22, zeroPad: 2, suffix: ""}), frameRate: 30, repeat: 0});
        
        // el cerdo 
        this.scene.anims.create({ key: "pig_running", frames: this.scene.anims.generateFrameNames( "texture_atlas_1", { prefix: "explosive_pig_", start: 1, end: 5, zeroPad: 2, suffix: ""}), frameRate: 20, repeat: -1});

        // el work in progress
        this.scene.anims.create({ key: "work_in_progress", frames: this.scene.anims.generateFrameNames( "texture_atlas_1", { prefix: "progress_", start: 1, end: 12, zeroPad: 2, suffix: ""}), frameRate: 24, repeat: -1});
     
        // enemies
        this.scene.anims.create({ key: "elemental_1", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_1_", start: 1, end: 5, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_2", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_2_", start: 1, end: 7, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_3", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_3_", start: 1, end: 10, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_4", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_4_", start: 1, end: 10, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_5", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_5_", start: 1, end: 10, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_6", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_6_", start: 1, end: 8, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_6_heal", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_6_heal_", start: 1, end: 18, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_7", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_7_", start: 1, end: 9, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_7_heal", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_7_heal_", start: 1, end: 18, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_8", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_8_", start: 1, end: 9, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_9", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_9_", start: 1, end: 9, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_10", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_10_", start: 1, end: 9, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_11", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_11_", start: 1, end: 11, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_12", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_12_", start: 1, end: 10, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_13", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_13_", start: 1, end: 10, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_14", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_14_", start: 1, end: 10, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_15", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_15_", start: 1, end: 9, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_16", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_16_", start: 1, end: 10, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_17", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_17_", start: 1, end: 9, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_18", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_18_", start: 1, end: 9, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_19", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_19_", start: 1, end: 9, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_20", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_20_", start: 1, end: 9, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
    
        this.scene.anims.create({ key: "elemental_tint_1", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_tint_1_", start: 1, end: 5, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_tint_2", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_tint_2_", start: 1, end: 7, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_tint_3", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_tint_3_", start: 1, end: 10, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_tint_4", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_tint_4_", start: 1, end: 10, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_tint_5", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_tint_5_", start: 1, end: 10, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_tint_6", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_tint_6_", start: 1, end: 8, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_tint_6_heal", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_tint_6_heal_", start: 1, end: 18, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_tint_7", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_tint_7_", start: 1, end: 9, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_tint_7_heal", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_tint_7_heal_", start: 1, end: 18, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_tint_8", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_tint_8_", start: 1, end: 9, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_tint_9", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_tint_9_", start: 1, end: 9, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_tint_10", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_tint_10_", start: 1, end: 9, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_tint_11", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_tint_11_", start: 1, end: 10, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_tint_12", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_tint_12_", start: 1, end: 9, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_tint_13", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_tint_13_", start: 1, end: 10, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_tint_14", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_tint_14_", start: 1, end: 10, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_tint_15", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_tint_15_", start: 1, end: 9, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_tint_16", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_tint_16_", start: 1, end: 10, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_tint_17", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_tint_17_", start: 1, end: 9, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_tint_18", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_tint_18_", start: 1, end: 9, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_tint_19", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_tint_19_", start: 1, end: 9, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        this.scene.anims.create({ key: "elemental_tint_20", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "elemental_tint_20_", start: 1, end: 9, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
        
        // deaths
        this.scene.anims.create({ key: "death_electric", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "death_electric_", start: 1, end: 5, zeroPad: 2, suffix: ""}), frameRate: 16});
        this.scene.anims.create({ key: "death_fire", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "death_fire_", start: 1, end: 5, zeroPad: 2, suffix: ""}), frameRate: 16});
        this.scene.anims.create({ key: "death_rock", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "death_rock_", start: 1, end: 5, zeroPad: 2, suffix: ""}), frameRate: 16});
        this.scene.anims.create({ key: "death_water", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "death_water_", start: 1, end: 5, zeroPad: 2, suffix: ""}), frameRate: 16});
        this.scene.anims.create({ key: "death_wind", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "death_wind_", start: 1, end: 5, zeroPad: 2, suffix: ""}), frameRate: 16});
        this.scene.anims.create({ key: "death_wood", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "death_wood_", start: 1, end: 5, zeroPad: 2, suffix: ""}), frameRate: 16});
        this.scene.anims.create({ key: "death_ice", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "death_ice_", start: 1, end: 5, zeroPad: 2, suffix: ""}), frameRate: 16});
        
        // turrets
        this.scene.anims.create({ key: "torre_1_bow_1", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "torre_1_bow_1_", start: 1, end: 4, zeroPad: 2, suffix: ""}), frameRate: 24, repeat: 0});
        this.scene.anims.create({ key: "torre_1_bow_2", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "torre_1_bow_2_", start: 1, end: 4, zeroPad: 2, suffix: ""}), frameRate: 24, repeat: 0});
        this.scene.anims.create({ key: "torre_1_bow_3", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "torre_1_bow_3_", start: 1, end: 4, zeroPad: 2, suffix: ""}), frameRate: 24, repeat: 0});

        this.scene.anims.create({ key: "torre_2_1", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "torre_2_1_", start: 1, end: 4, zeroPad: 2, suffix: ""}), frameRate: 24, repeat: 0});
        this.scene.anims.create({ key: "torre_2_2", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "torre_2_2_", start: 1, end: 4, zeroPad: 2, suffix: ""}), frameRate: 24, repeat: 0});
        this.scene.anims.create({ key: "torre_2_3", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "torre_2_3_", start: 1, end: 4, zeroPad: 2, suffix: ""}), frameRate: 24, repeat: 0});

        this.scene.anims.create({ key: "torre_3_1", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "torre_3_1_", start: 1, end: 5, zeroPad: 2, suffix: ""}), frameRate: 12, repeat: 0});
        this.scene.anims.create({ key: "torre_3_2", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "torre_3_2_", start: 1, end: 5, zeroPad: 2, suffix: ""}), frameRate: 12, repeat: 0});
        this.scene.anims.create({ key: "torre_3_3", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "torre_3_3_", start: 1, end: 5, zeroPad: 2, suffix: ""}), frameRate: 12, repeat: 0});

        this.scene.anims.create({ key: "torre_4_1", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "torre_4_1_", start: 1, end: 6, zeroPad: 2, suffix: ""}), frameRate: 12, repeat: -1});
        this.scene.anims.create({ key: "torre_4_2", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "torre_4_2_", start: 1, end: 6, zeroPad: 2, suffix: ""}), frameRate: 12, repeat: -1});
        this.scene.anims.create({ key: "torre_4_3", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "torre_4_3_", start: 1, end: 6, zeroPad: 2, suffix: ""}), frameRate: 12, repeat: -1});

        this.scene.anims.create({ key: "torre_5_1", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "torre_5_1_", start: 1, end: 15, zeroPad: 2, suffix: ""}), frameRate: 12, repeat: 0});
        this.scene.anims.create({ key: "torre_5_2", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "torre_5_2_", start: 1, end: 15, zeroPad: 2, suffix: ""}), frameRate: 12, repeat: 0});
        this.scene.anims.create({ key: "torre_5_3", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "torre_5_3_", start: 1, end: 15, zeroPad: 2, suffix: ""}), frameRate: 12, repeat: 0});

        this.scene.anims.create({ key: "torre_6_1", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "torre_6_1_", start: 1, end: 9, zeroPad: 2, suffix: ""}), frameRate: 12, repeat: 0});
        this.scene.anims.create({ key: "torre_6_2", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "torre_6_2_", start: 1, end: 9, zeroPad: 2, suffix: ""}), frameRate: 12, repeat: 0});
        this.scene.anims.create({ key: "torre_6_3", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "torre_6_3_", start: 1, end: 9, zeroPad: 2, suffix: ""}), frameRate: 12, repeat: 0});
        this.scene.anims.create({ key: "torre_6_municion_suelo", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "torre_6_municion_suelo_", start: 1, end: 7, zeroPad: 2, suffix: ""}), frameRate: 12, repeat: -1});

        this.scene.anims.create({ key: "torre_7_1", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "torre_7_1_", start: 1, end: 8, zeroPad: 2, suffix: ""}), frameRate: 12, repeat: -1});
        this.scene.anims.create({ key: "torre_7_2", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "torre_7_2_", start: 1, end: 8, zeroPad: 2, suffix: ""}), frameRate: 12, repeat: -1});
        this.scene.anims.create({ key: "torre_7_3", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "torre_7_3_", start: 1, end: 8, zeroPad: 2, suffix: ""}), frameRate: 12, repeat: -1});
        this.scene.anims.create({ key: "torre_7_fx", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "torre_7_fx_", start: 1, end: 8, zeroPad: 2, suffix: ""}), frameRate: 12, repeat: -1});

        this.scene.anims.create({ key: "torre_8_1", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "torre_8_1_", start: 1, end: 8, zeroPad: 2, suffix: ""}), frameRate: 12, repeat: -1});
        this.scene.anims.create({ key: "torre_8_2", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "torre_8_2_", start: 1, end: 8, zeroPad: 2, suffix: ""}), frameRate: 12, repeat: -1});
        this.scene.anims.create({ key: "torre_8_3", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "torre_8_3_", start: 1, end: 8, zeroPad: 2, suffix: ""}), frameRate: 12, repeat: -1});
    }
}
